/** @forge-header
 * @id          nbaParsers
 * @role        NBA ticker parsers | game moneyline + season props
 * @pattern     pure-fn
 * @purity      none
 * @units       n/a
 * @upstream    kalshiTicker/types:ParsedTicker,GameInfo | nbaConsolidated/teamNames:teamNameFromCode
 * @downstream  kalshiTicker/parse
 * @exports     parseNbaGame:fn | parseNbaSeason:fn
 * @test        parse.test.ts (indirect)
 */

import type { ParsedTicker, GameInfo } from '../types';
import { parseDateFromTicker } from '../types';
import { parseFallback } from '../helpers';
import { teamNameFromCode } from '../teamNames';

/** NBA game moneyline: KXNBAGAME-26JAN15BOSMIA-BOS */
export function parseNbaGame(raw: string): ParsedTicker {
  const parts = raw.includes('--') ? raw.split('--') : raw.split('-');

  let eventPart: string;
  let teamCode: string | null = null;

  if (raw.includes('--')) {
    eventPart = parts[0] ?? '';
    teamCode = parts[1] ?? null;
  } else {
    teamCode = parts[parts.length - 1] ?? null;
    eventPart = parts.slice(0, -1).join('-');
  }

  const eventParts = eventPart.split('-');
  const mid = eventParts[1] ?? '';

  if (mid.length < 13) {
    return parseFallback(raw);
  }

  const date = parseDateFromTicker(mid.slice(0, 7));
  const teamCodes = mid.slice(7);

  if (teamCodes.length !== 6) {
    return parseFallback(raw);
  }

  const awayCode = teamCodes.slice(0, 3);
  const homeCode = teamCodes.slice(3, 6);

  const game: GameInfo = {
    awayCode,
    homeCode,
    awayName: teamNameFromCode(awayCode, 'nba'),
    homeName: teamNameFromCode(homeCode, 'nba'),
  };

  const shortLabel = `NBA: ${awayCode} @ ${homeCode}`;
  const fullLabel = teamCode
    ? `NBA: ${game.awayName ?? awayCode} @ ${game.homeName ?? homeCode} · ${teamCode} Win`
    : `NBA: ${game.awayName ?? awayCode} @ ${game.homeName ?? homeCode}`;

  return {
    raw,
    category: 'nba-game',
    series: 'KXNBAGAME',
    date,
    game,
    player: null,
    spread: null,
    total: null,
    tennis: null,
    outcome: teamCode,
    shortLabel,
    fullLabel,
  };
}

/** NBA season props: KXNBA-26-SAS */
export function parseNbaSeason(raw: string): ParsedTicker {
  const parts = raw.split('-');
  const year = parts[1] ? `20${parts[1]}` : null;
  const outcome = parts[2] ?? null;

  const shortLabel = outcome ? `NBA Season: ${outcome}` : 'NBA Season Prop';
  const fullLabel = year ? `NBA ${year} Season${outcome ? ` · ${outcome}` : ''}` : shortLabel;

  return {
    raw,
    category: 'nba-season',
    series: 'KXNBA',
    date: null,
    game: null,
    player: null,
    spread: null,
    total: null,
    tennis: null,
    outcome,
    shortLabel,
    fullLabel,
  };
}
